/**
 * Handles ID collisions by adjusting IDs to high ranges
 * and mapping them back to original words/blocks when saving
 */

export interface IdMapping {
  adjustedId: string | number;
  originalId: string | number;
  occurrenceIndex: number; // Which occurrence of the original ID (0 = first, 1 = second, etc.)
}

/**
 * Adjusts an ID if it collides with existing IDs
 * Uses ranges: 2000000, 3000000, 4000000, etc.
 */
export function adjustIdIfCollision(
  originalId: string | number,
  existingIds: Set<string | number>
): { adjustedId: string | number; wasAdjusted: boolean } {
  // If ID doesn't exist, no adjustment needed
  if (!existingIds.has(originalId)) {
    return { adjustedId: originalId, wasAdjusted: false };
  }

  // Try ranges: 2000000, 3000000, 4000000, etc.
  let rangeOffset = 2000000;
  let adjustedId: string | number = originalId;

  // Check if original ID is numeric
  const originalIdNum = typeof originalId === 'string' ? parseInt(originalId, 10) : originalId;
  const isNumeric = !isNaN(originalIdNum as number);

  if (!isNumeric) {
    // Non-numeric IDs: append suffix
    let suffix = 1;
    do {
      adjustedId = `${originalId}_${suffix}`;
      suffix++;
      if (suffix > 1000) {
        // Safety limit
        throw new Error(`Cannot find unique ID for non-numeric ID: ${originalId}`);
      }
    } while (existingIds.has(adjustedId));
    return { adjustedId, wasAdjusted: true };
  }

  // Numeric IDs: add range offsets
  while (existingIds.has(adjustedId)) {
    adjustedId = originalIdNum + rangeOffset;
    rangeOffset += 1000000; // Next range: 3000000, 4000000, etc.
    
    if (rangeOffset > 10000000) {
      // Safety limit
      throw new Error(`Cannot find unique ID for numeric ID: ${originalId}`);
    }
  }

  return { adjustedId, wasAdjusted: true };
}

/**
 * Checks if an ID is an adjusted ID (>= 1000000 for numeric, or has suffix for non-numeric)
 */
export function isAdjustedId(id: string | number): boolean {
  if (typeof id === 'string') {
    // Check if it's a non-numeric ID with suffix (e.g., "abc_1")
    return /_\d+$/.test(id);
  }
  
  // Numeric: check if >= 1000000
  return typeof id === 'number' && id >= 1000000;
}

/**
 * Extracts the original ID and occurrence index from an adjusted ID
 */
export function extractOriginalIdInfo(
  adjustedId: string | number,
  originalIds: (string | number)[]
): { originalId: string | number; occurrenceIndex: number } | null {
  if (!isAdjustedId(adjustedId)) {
    // Not an adjusted ID - check if it exists in originalIds
    if (originalIds.includes(adjustedId)) {
      return { originalId: adjustedId, occurrenceIndex: 0 };
    }
    // Not an adjusted ID and not in originalIds - invalid
    return null;
  }

  if (typeof adjustedId === 'string') {
    // Non-numeric with suffix: "abc_1" -> originalId: "abc", occurrenceIndex: 1 (second occurrence)
    const match = adjustedId.match(/^(.+)_(\d+)$/);
    if (!match) {
      // Not a valid adjusted ID format - check if it's in originalIds, if not return null
      if (originalIds.includes(adjustedId)) {
        return { originalId: adjustedId, occurrenceIndex: 0 };
      }
      return null;
    }
    const originalId = match[1];
    const suffix = parseInt(match[2], 10);
    
    // Suffix is 1-based, occurrenceIndex is 0-based
    // "abc_1" means it's the 1st duplicate (occurrenceIndex 1 = second word with that ID)
    // occurrenceIndex 0 = first occurrence, 1 = second occurrence, etc.
    const occurrenceIndex = suffix;
    
    return { originalId, occurrenceIndex };
  }

  // Numeric: extract by subtracting range offsets
  const adjustedNum = adjustedId;
  
  // Try ranges: 2000000, 3000000, 4000000, etc.
  let rangeOffset = 2000000;
  let originalId: number | null = null;

  while (rangeOffset <= 10000000) {
    if (adjustedNum >= rangeOffset && adjustedNum < rangeOffset + 1000000) {
      originalId = adjustedNum - rangeOffset;
      
      // Calculate which occurrence this is based on the range
      // 2000000 = 0th occurrence, 3000000 = 1st occurrence, etc.
      const occurrenceIndex = (rangeOffset - 2000000) / 1000000;
      
      return { originalId, occurrenceIndex };
    }
    rangeOffset += 1000000;
  }

  return null;
}

/**
 * Finds the nth occurrence of an ID in an array
 */
export function findNthOccurrence<T>(
  items: T[],
  idExtractor: (item: T) => string | number,
  targetId: string | number,
  occurrenceIndex: number
): T | null {
  let count = 0;
  for (const item of items) {
    const itemId = idExtractor(item);
    if (itemId === targetId || String(itemId) === String(targetId)) {
      if (count === occurrenceIndex) {
        return item;
      }
      count++;
    }
  }
  return null;
}

/**
 * Gets the next available ID from a list of existing IDs
 */
export function getNextAvailableId(
  existingIds: (string | number)[]
): number {
  if (existingIds.length === 0) {
    return 1;
  }

  // Find max numeric ID
  let maxId = 0;
  for (const id of existingIds) {
    const idNum = typeof id === 'string' ? parseInt(id, 10) : id;
    if (!isNaN(idNum as number) && idNum > maxId && idNum < 1000000) {
      maxId = idNum;
    }
  }

  return maxId + 1;
}

